import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { NoPermissionPage, NoRolePage } from '@/app/pages/no-permission-page';
import { requirementFor } from '@/app/route-permissions';
import { hasModule, holds, holdsArea, useCapabilities } from '@/lib/permissions';
import { consumeSessionEnded, useMe } from '@/lib/session';

/**
 * GEN-159: a screen still loading shows grey shimmering blocks in the shape of
 * the screen, never a spinner in a void — this is the shell-shaped one used
 * while /api/me resolves.
 *
 * NFR-43's frame half — "every screen must show that it is working while it
 * loads, and must never show a blank region with no explanation": this
 * skeleton is what stands where the whole shell will be, so no route ever
 * opens onto a blank frame. The per-screen half — each screen's own reads —
 * is population-checked by `nfr43-loading-states.test.tsx`.
 */
function ShellSkeleton() {
  return (
    <div className="flex h-screen overflow-hidden" aria-busy>
      <div className="hidden h-full w-64 shrink-0 border-e border-border p-4 md:block">
        <div className="mb-8 h-9 animate-pulse rounded-md bg-muted" />
        <div className="grid gap-2">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="h-8 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      </div>
      <div className="flex-1 p-6">
        <div className="mb-6 h-10 w-1/3 animate-pulse rounded-md bg-muted" />
        <div className="h-64 animate-pulse rounded-xl bg-muted" />
      </div>
    </div>
  );
}

/**
 * Wraps the app layout: no session → GEN-S1. When a session existed earlier in
 * this browser, the bounce carries the "session ended" flag so sign-in can
 * show the message GEN-S1 §3 proposes; a first-time visitor sees a plain form.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { data: me, isPending } = useMe();

  if (isPending) return <ShellSkeleton />;
  if (!me) {
    return (
      <Navigate to="/sign-in" replace state={consumeSessionEnded() ? { sessionEnded: true } : null} />
    );
  }
  return children;
}

/**
 * THE ROUTE-LEVEL PERMISSION GATE — PART 16.7 and the client half of
 * BR-PERM-51.
 *
 * "Every route must enforce its own permission when it is opened, regardless
 * of how the person arrived — menu, typed address, bookmark, notification
 * link, or back button." The server has always done that. What this adds is
 * the second sentence of 16.7 row 1: "If they type the address, show the 'no
 * permission' page." Before this, typing `/employees/payroll` rendered the
 * payroll screen, fired its reads, collected a 403 and drew a banner inside a
 * screen the person may not open — technically safe, and a lie about where
 * they were standing.
 *
 * WHY THIS IS NOT THE CHECK, in one line each:
 *   - it runs in the browser, where the user owns every byte;
 *   - `PermissionGuard` and `EntitlementsGuard` refuse every request whatever
 *     this decides (BR-PERM-49, BR-PERM-50), and nothing about them changed;
 *   - it draws optimistically while /api/me is still in flight, which a
 *     control could never do.
 *
 * It sits INSIDE the shell rather than around it, on purpose: a refused person
 * keeps their menu, their company name and their way back out. 16.7's page is
 * a page, not a wall.
 */
export function RequirePermission({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const caps = useCapabilities();
  const need = requirementFor(pathname);

  // Until the session lands, draw. RequireAuth already holds the shell until
  // /api/me resolves, so in the app this is only ever true for a moment; in a
  // test that renders a screen directly it is the difference between an
  // optimistic render and a false refusal.
  if (caps.loading) return children;

  // 16.7 row 4 / BR-PERM-73: a person with no role at all is not shown an
  // empty shell. They land here — on the landing screen, which is the one
  // route their near-zero floor reaches — and are told why and who to ask.
  if (!caps.isOwner && caps.permissions.size === 0 && pathname === '/') return <NoRolePage />;

  // §1.5.2: "permission is checked first, then the module flag." The two
  // produce the same page, so the order changes nothing a user sees — but it
  // is the order the spec fixes, and the day the two pages differ it will be
  // the reason they differ correctly.
  //
  // GEN-21a/GEN-21b at the door: shown only when the module is on AND the
  // role grants it, no third test — and a dead module answers the same for
  // every role, Owner included (no permission reveals an unbought module).
  // GEN-18a rides the same table: route-permissions.ts derives each route's
  // requirement from the SAME NAV_GROUPS rows the menu draws from, so menu
  // absence and access refusal cannot disagree. (GEN-21c's different-message
  // demand is the recorded §1.5.2 contradiction — see no-permission-page.)
  // R18/BR-PERM-69 — the Owner flag, checked before anything a role could
  // satisfy. ADM-07 is the only route with this requirement, and it is not
  // reachable by holding more permissions.
  if (need.ownerOnly === true && !caps.isOwner) return <NoPermissionPage />;

  // `areas` and `extras` are each held-ANY-of and are OR'd together — unchanged.
  const opens =
    need.areas.length === 0 && need.extras === undefined
      ? true
      : need.areas.some((area) => holdsArea(caps, area)) ||
        (need.extras?.some((permission) => holds(caps, permission)) ?? false);

  /**
   * …AND every permission in `also`, when a route names any.
   *
   * This is the file's only conjunction and it exists because the reports
   * centres demand two permissions on the server — `reports.view` at the
   * controller and the centre's own `<area>.view` inside the service — and a
   * disjunction cannot describe that. Naming either one alone admits somebody
   * the other wall then refuses, in a screen they should never have opened.
   *
   * `?? true` rather than a length check: ABSENT and EMPTY must mean the same
   * thing here, because an `also: []` that silently refused everyone would be
   * the worst failure this layer can produce — a screen nobody can open, with
   * no permission that fixes it. Every rule but the four report centres omits
   * the field entirely, so this branch is the identity for all of them.
   */
  const permitted = opens && (need.also?.every((permission) => holds(caps, permission)) ?? true);
  if (!permitted) return <NoPermissionPage />;

  if (need.module !== undefined && !hasModule(caps, need.module)) return <NoPermissionPage />;

  return children;
}

/**
 * Wraps GEN-S1/GEN-S2: an already-signed-in visitor goes straight to the
 * shell — the sign-in screen is for proving identity, not something a session
 * holder ever needs to see.
 */
export function PublicOnly({ children }: { children: ReactNode }) {
  const { data: me, isPending } = useMe();

  if (isPending) return null;
  if (me) return <Navigate to="/" replace />;
  return children;
}
